import {
  Component,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  input,
  output,
  Output,
  EventEmitter,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import {
  CONTACT_LIST_DETAIL_CONTAINER,
  CREATE_CONTACT_LIST_CONTAINER,
} from 'src/app/constants/common.constants';
import {
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import {
  ContactList,
  ContactListSortOrQuery,
} from 'src/app/model/contacts/contact-list';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { ContactListBuilderService } from 'src/app/services/builder/contact-list-builder.service';
import { ContactListTableService } from 'src/app/services/table/contact-list-table.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { getContactListColumns } from '../../definitions/contact-lists-table-definition';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { Subscription } from 'rxjs';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { ContactListSearch } from 'src/app/model/contacts/contact-list-search-results';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../../core/components/business-unit-filter/business-unit-filter.component';
import { ToggleSwitch } from '@openng/optimus-ui/toggleswitch';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { Panel } from '@openng/optimus-ui/panel';
import { ContactListMobileFilterComponent } from '../../components/contact-list-mobile-filter/contact-list-mobile-filter.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';

@Component({
  selector: 'app-contact-list-search-container',
  templateUrl: './contact-list-search-container.component.html',
  styleUrls: ['./contact-list-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    BusinessUnitFilterComponent_1,
    ToggleSwitch,
    FormsModule,
    ButtonDirective,
    Panel,
    ContactListMobileFilterComponent,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class ContactListSearchContainerComponent
  extends SearchContainerComponent<ContactList>
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _contactListApi = inject(ContactListApiService);
  protected _contactListBuilder = inject(ContactListBuilderService);
  protected _contactListTableService = inject(ContactListTableService);
  protected _authApi = inject(AuthApiService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);

  readonly embedded = input(false);
  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  readonly selectedListEvent = output<any>();

  allContactLists: any[] = [];
  contactsLoading: boolean = true;
  availableCompanies: any[] = [];
  @Output() contactsLoadingEvent = new EventEmitter<any>();
  canCreate = signal(false);
  scrollHeight = 'calc(100vh - 256px)';
  mobileFilterCollapsed = true;
  activeTabChangedSub!: Subscription;
  showAdditionalResourceLists: boolean = false;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setupActiveTabChangedSubscription();
    this.setTableService(this._contactListTableService);
    this.initialize();
  }

  initialize() {
    this.canCreate.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTACTS.CONTACT_LISTS.CREATE_CONTACT_LISTS
      )
    );
    if (this.embedded() === false) {
      // setup the bread crumb.
      this._translateService
        .get('CONTACT.SCREEN.CONTACT_LIST')
        .subscribe((label) => {
          if (this._deviceService.isMobile()) {
            this._breadCrumbBuilderService.resetAndAddBreadCrumb(
              new BreadCrumb(label, null, false)
            );
          } else {
            this._tabService.updateActiveTabLabel(label);
          }
          this.screenName = label;
        });
    }

    if (this._deviceService.isMobile()) {
      this.scrollHeight = 'calc(100vh - 180px)';
    }

    const options = [
      { label: 'Choose', value: null },
      { label: 'True', value: true },
      { label: 'False', value: false },
    ];

    this.columns = getContactListColumns(options);
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  /**
   * Method called when the user hits the clear button.
   */
  clearTable(): void {
    this.clearScreen();
    this.showAdditionalResourceLists = false;
  }

  /**
   * Method called when a row double click event is fired.  This navigates to the details page.
   * @param $event - Event from the double click event.
   */
  changeViewEvent($event: { data: ContactListSearch }) {
    if (this.embedded() === false) {
      this._contactListTableService.setSelected(null); // make sure we grab by id after it opens
      const tab = this._tabService.buildNewTab(
        CONTACT_LIST_DETAIL_CONTAINER,
        true,
        undefined,
        $event.data.id
      );
      if (this._deviceService.isMobile()) {
        this._tabService.setMobileTab(tab);
      } else {
        this._tabService.openTab(tab);
      }
    } else {
      this.selectedListEvent.emit($event);
    }
  }

  newContactList() {
    const tab = this._tabService.buildNewTab(
      CREATE_CONTACT_LIST_CONTAINER,
      true
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'name');

    this._contactListApi
      .queryForContactLists(
        req.pageSize,
        req.page,
        query as ContactListSortOrQuery,
        sort
      )
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          this.totalRecords = data.totalRecords;
          this._contactListTableService.setLastTotalRecords(this.totalRecords);
          this.allContactLists = data.items;
          this.elements = [...this.allContactLists];
          this.loading = false;
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'CONTACT.MESSAGES.ERROR.RETRIEVE_CONTACT_LISTS',
          });
          this.totalRecords = 0;
          this.allContactLists = [];
          this.elements = [];
          this.loading = false;
        },
      });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: any
  ) {
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    filters['businessUnits'] = selectedBusinessUnits;
    filters['additionalResourceLists'] = this.showAdditionalResourceLists;

    const query = this._contactListBuilder.buildContactListQuery(filters);
    this._contactListTableService.setLastQuery(query);
    this.queryNetwork(req, $event, query);
  }

  mobileFilter($event: any) {
    const newLazy: any = {
      filters: {
        ...this.grid()?.table()?.filters,
        state: $event.state
          ? {
              value: $event.state,
              matchMode: 'contains',
            }
          : null,
        county: $event.county
          ? {
              value: $event.county,
              matchMode: 'contains',
            }
          : null,
        township: $event.township
          ? {
              value: $event.township,
              matchMode: 'contains',
            }
          : null,
      },
    };
    this.mobileFilterCollapsed = true;
    this._contactListTableService.setLastLazyLoad(newLazy);
    this.lazyLoad({ lazy: this._contactListTableService.getLastLazyLoad()! });
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._contactListTableService.getLastLazyLoad()! });
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.allContactLists = [];
          this.refresh();
        }
      }
    );
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._contactListTableService.getLastLazyLoad()! });
  }
}
